<template>
  <div :key="localeRenderKey" class="welcome-container">
    <div class="welcome-card">
      <TracktorLogoSection
        :title-prefix="s__('Welcome|Welcome to')"
        :subtitle="s__('Welcome|Drive Your Projects Forward')"
      />

      <div class="info-section">
        <div v-if="!languageStepCompleted" class="language-step">
          <h2 class="language-step-title">{{ s__('Welcome|Choose language') }}</h2>
          <div class="language-step-options">
            <button
              class="language-option"
              :class="{ selected: selectedLocale === 'en' }"
              type="button"
              @click="chooseLocaleAndProceed('en')"
            >
              {{ s__('Welcome|English') }}
            </button>
            <button
              class="language-option"
              :class="{ selected: selectedLocale === 'ru' }"
              type="button"
              @click="chooseLocaleAndProceed('ru')"
            >
              {{ s__('Welcome|Russian') }}
            </button>
          </div>
        </div>

        <template v-else>
          <div class="language-switch">
            <button
              class="language-switch-button"
              :class="{ selected: selectedLocale === 'en' }"
              type="button"
              @click="selectLocale('en')"
            >
              EN
            </button>
            <button
              class="language-switch-button"
              :class="{ selected: selectedLocale === 'ru' }"
              type="button"
              @click="selectLocale('ru')"
            >
              RU
            </button>
          </div>

          <p class="info-text">
            {{ s__('Welcome|To use Tracktor, authenticate with Telegram or email') }}
          </p>

          <div class="email-auth-section">
            <h3 class="email-auth-title">{{ s__('Welcome|Email authentication') }}</h3>

            <div v-if="authSuccessMessage" class="auth-notice">{{ authSuccessMessage }}</div>
            <div v-if="authError" class="auth-error">{{ authError }}</div>

            <div class="email-auth-actions mobile-stretch-btn-group">
              <button
                class="email-auth-button secondary mobile-stretch-btn"
                type="button"
                :disabled="loadingAny"
                @click="switchModeAndFocusInput(isTelegramOnboarding ? 'telegramSignup' : 'signup')"
              >
                {{
                  isTelegramOnboarding
                    ? s__('Welcome|Register new account')
                    : s__('Welcome|Sign up with email')
                }}
              </button>
              <button
                class="email-auth-button mobile-stretch-btn"
                type="button"
                :disabled="loadingAny"
                @click="switchModeAndFocusInput('signin')"
              >
                {{
                  isTelegramOnboarding
                    ? s__('Welcome|I already have an account')
                    : s__('Welcome|Sign in with email')
                }}
              </button>
            </div>

            <EmailSignUpForm
              v-if="authMode === 'signup'"
              :email="signUpForm.email"
              :username="signUpForm.username"
              :full-name="signUpForm.fullName"
              :email-required="true"
              :disabled="loadingAny"
              :submitting="loadingSignUp"
              @update:email="value => (signUpForm.email = value)"
              @update:username="value => (signUpForm.username = value)"
              @update:full-name="value => (signUpForm.fullName = value)"
              @submit="submitSignUp"
            />

            <EmailSignUpForm
              v-if="authMode === 'telegramSignup'"
              :email="signUpForm.email"
              :username="signUpForm.username"
              :full-name="signUpForm.fullName"
              :email-required="false"
              :disabled="loadingAny"
              :submitting="loadingSignUp"
              @update:email="value => (signUpForm.email = value)"
              @update:username="value => (signUpForm.username = value)"
              @update:full-name="value => (signUpForm.fullName = value)"
              @submit="submitTelegramOnboardingSignUp"
            />

            <form
              v-if="authMode === 'signin'"
              class="email-auth-form"
              @submit.prevent="submitRequestCode"
            >
              <label class="email-auth-label">
                {{ s__('Welcome|Email or username') }} *
                <input
                  v-model.trim="signInForm.login"
                  class="email-auth-input"
                  type="text"
                  required
                />
              </label>
              <div v-if="resendInfo" class="auth-notice">{{ resendInfo }}</div>
              <div class="email-auth-form-actions">
                <button class="email-auth-submit" type="submit" :disabled="loadingAny">
                  {{
                    loadingRequestCode
                      ? s__('Welcome|Sending code...')
                      : s__('Welcome|Send sign-in code')
                  }}
                </button>
              </div>
            </form>

            <form
              v-if="authMode === 'code'"
              class="email-auth-form"
              @submit.prevent="submitVerifyCode"
            >
              <label class="email-auth-label">
                {{ s__('Welcome|Code from email') }} *
                <input
                  v-model.trim="signInForm.code"
                  class="email-auth-input"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  required
                />
              </label>
              <div class="email-auth-form-actions">
                <button class="email-auth-submit" type="submit" :disabled="loadingAny">
                  {{
                    loadingVerifyCode
                      ? s__('Welcome|Verifying...')
                      : s__('Welcome|Verify and sign in')
                  }}
                </button>
                <button
                  class="email-auth-cancel"
                  type="button"
                  :disabled="loadingAny"
                  @click="switchMode('signin')"
                >
                  {{ s__('Welcome|Back') }}
                </button>
              </div>
            </form>
          </div>

          <div class="telegram-auth-methods">
            <h3 class="telegram-auth-title">{{ s__('Welcome|Telegram authentication') }}</h3>
            <div class="telegram-auth-methods-grid">
              <!-- Telegram Mini App Method -->
              <div class="telegram-auth-method">
                <div class="method-icon">📱</div>
                <h3 class="method-title">{{ s__('Welcome|Using Telegram Mini App') }}</h3>
                <ol class="method-steps">
                  <li>{{ s__('Welcome|Open Telegram app on your device') }}</li>
                  <li>{{ s__('Welcome|Find') }} {{ botUsername }}</li>
                  <li>{{ s__('Welcome|Click "Menu" button or type /start') }}</li>
                  <li>{{ s__('Welcome|Launch the mini-app from the menu') }}</li>
                </ol>
              </div>

              <!-- Browser Authentication Method -->
              <div class="telegram-auth-method">
                <div class="method-icon">🌐</div>
                <h3 class="method-title">{{ s__('Welcome|Using Browser') }}</h3>
                <ol class="method-steps">
                  <li>{{ s__('Welcome|Open Telegram app on your device') }}</li>
                  <li>{{ s__('Welcome|Find') }} {{ botUsername }}</li>
                  <li>
                    {{ s__('Welcome|Send /browser_auth command to the bot') }}
                  </li>
                  <li>{{ s__('Welcome|Click the authentication link you receive') }}</li>
                  <li>
                    {{ s__('Welcome|The link expires in 5 minutes for security') }}
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div class="help-section">
            <p class="help-text">
              {{ s__('Welcome|Need help? Contact us via') }} {{ botUsername }}
              {{ s__('Welcome|or via email: support@tracktor.space') }}
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { loadTranslations, s__ } from '@/locale'
import EmailSignUpForm from '@/components/common/EmailSignUpForm.vue'
import TracktorLogoSection from '@/components/common/TracktorLogoSection.vue'

const LOCALE_STORAGE_KEY = 'tracktor.welcome_locale'
const SUPPORTED_LOCALES = ['en', 'ru']

const BOT_USERNAME_Q = gql`
  query GetBotUsername {
    botUsername
    telegramOnboardingState {
      pending
      telegramUserId
      username
      fullName
      preferredLanguage
      preferredTimezone
    }
  }
`

const EMAIL_SIGN_UP = gql`
  mutation EmailSignUp(
    $email: String!
    $username: String!
    $fullName: String!
    $preferredLanguage: String!
  ) {
    emailSignUp(
      email: $email
      username: $username
      fullName: $fullName
      preferredLanguage: $preferredLanguage
    ) {
      success
      errors
    }
  }
`

const REQUEST_EMAIL_SIGN_IN_CODE = gql`
  mutation RequestEmailSignInCode($login: String!) {
    requestEmailSignInCode(login: $login) {
      success
      challengeId
      resendAvailableAt
      errors
    }
  }
`

const VERIFY_EMAIL_SIGN_IN_CODE = gql`
  mutation VerifyEmailSignInCode($challengeId: ID!, $code: String!) {
    verifyEmailSignInCode(challengeId: $challengeId, code: $code) {
      success
      errors
    }
  }
`

const CONFIRM_EMAIL = gql`
  mutation ConfirmEmail($token: String!) {
    confirmEmail(token: $token) {
      success
      errors
    }
  }
`

const TELEGRAM_ONBOARDING_SIGN_UP = gql`
  mutation TelegramOnboardingSignUp(
    $username: String!
    $fullName: String!
    $preferredLanguage: String
    $email: String
  ) {
    telegramOnboardingSignUp(
      username: $username
      fullName: $fullName
      preferredLanguage: $preferredLanguage
      email: $email
    ) {
      success
      errors
      user {
        id
      }
    }
  }
`

const { result: botUsernameResult } = useQuery(BOT_USERNAME_Q)
const { mutate: emailSignUpMutate } = useMutation(EMAIL_SIGN_UP)
const { mutate: requestCodeMutate } = useMutation(REQUEST_EMAIL_SIGN_IN_CODE)
const { mutate: verifyCodeMutate } = useMutation(VERIFY_EMAIL_SIGN_IN_CODE)
const { mutate: confirmEmailMutate } = useMutation(CONFIRM_EMAIL)
const { mutate: telegramOnboardingSignUpMutate } = useMutation(TELEGRAM_ONBOARDING_SIGN_UP)

const authMode = ref(null)
const authError = ref('')
const authSuccessMessage = ref('')
const loadingSignUp = ref(false)
const loadingRequestCode = ref(false)
const loadingVerifyCode = ref(false)
const challengeId = ref(null)
const resendAvailableAt = ref(null)
const signUpForm = ref({
  email: '',
  username: '',
  fullName: '',
})
const signInForm = ref({
  login: '',
  code: '',
})
const selectedLocale = ref('en')
const languageStepCompleted = ref(false)
const localeRenderKey = ref(0)

const telegramOnboardingState = computed(
  () => botUsernameResult.value?.telegramOnboardingState || null
)
const isTelegramMiniAppContext = computed(() => {
  const webApp = window.Telegram?.WebApp
  const telegramUserId = webApp?.initDataUnsafe?.user?.id
  const hasInitData = typeof webApp?.initData === 'string' && webApp.initData.length > 0
  return Boolean(telegramUserId || hasInitData)
})
const isTelegramOnboarding = computed(
  () => telegramOnboardingState.value?.pending === true || isTelegramMiniAppContext.value
)

const botUsername = computed(() => {
  const username = botUsernameResult.value?.botUsername
  return username ? `@${username}` : '@TracktorBot'
})

const loadingAny = computed(
  () => loadingSignUp.value || loadingRequestCode.value || loadingVerifyCode.value
)

const resendInfo = computed(() => {
  if (!resendAvailableAt.value) return ''
  return s__('Welcome|If needed, you can request a new code after %{time}').replace(
    '%{time}',
    new Date(resendAvailableAt.value).toLocaleTimeString()
  )
})

const clearMessages = () => {
  authError.value = ''
  authSuccessMessage.value = ''
}

const normalizeLocale = locale => {
  const normalized = locale?.toString().toLowerCase().split('-')[0]
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : 'en'
}

const detectLocale = () => {
  const savedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY)
  if (savedLocale) {
    return normalizeLocale(savedLocale)
  }

  const telegramLocale = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
  if (telegramLocale) {
    return normalizeLocale(telegramLocale)
  }

  return normalizeLocale(window.navigator.language)
}

const applyLocale = locale => {
  const normalizedLocale = normalizeLocale(locale)
  selectedLocale.value = normalizedLocale
  window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLocale)
  document.documentElement.lang = normalizedLocale
  loadTranslations(normalizedLocale)
  localeRenderKey.value += 1
}

const selectLocale = locale => {
  applyLocale(locale)
}

const chooseLocaleAndProceed = locale => {
  applyLocale(locale)
  languageStepCompleted.value = true
}

const switchMode = (mode, preserveMessages = false) => {
  if (!preserveMessages) {
    clearMessages()
  }
  authMode.value = mode
  if (mode !== 'code') {
    signInForm.value.code = ''
  }
}

const switchModeAndFocusInput = async mode => {
  switchMode(mode)

  for (let attempt = 0; attempt < 5; attempt += 1) {
    await nextTick()
    await new Promise(resolve => window.requestAnimationFrame(resolve))

    const input = document.querySelector('.email-auth-section .email-auth-form .email-auth-input')
    if (!input) continue

    const rect = input.getBoundingClientRect()
    const targetTop = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
    input.scrollIntoView({ block: 'center', behavior: 'smooth' })
    input.focus({ preventScroll: true })
    return
  }
}

const submitSignUp = async () => {
  clearMessages()
  loadingSignUp.value = true
  try {
    const response = await emailSignUpMutate({
      email: signUpForm.value.email,
      username: signUpForm.value.username,
      fullName: signUpForm.value.fullName,
      preferredLanguage: selectedLocale.value,
    })
    const result = response?.data?.emailSignUp
    if (!result?.success) {
      authError.value = result?.errors?.[0] || s__('Welcome|Unable to sign up')
      return
    }

    authSuccessMessage.value = s__(
      'Welcome|Account created. Check your email and confirm it to activate sign-in.'
    )
    switchMode('signin', true)
  } catch (e) {
    authError.value = e.message || s__('Welcome|Unable to sign up')
  } finally {
    loadingSignUp.value = false
  }
}

const submitTelegramOnboardingSignUp = async () => {
  clearMessages()
  loadingSignUp.value = true
  try {
    const response = await telegramOnboardingSignUpMutate({
      email: signUpForm.value.email || null,
      username: signUpForm.value.username,
      fullName: signUpForm.value.fullName,
      preferredLanguage: selectedLocale.value,
    })
    const result = response?.data?.telegramOnboardingSignUp
    if (!result?.success) {
      authError.value = result?.errors?.[0] || s__('Welcome|Unable to sign up')
      return
    }

    window.location.reload()
  } catch (e) {
    authError.value = e.message || s__('Welcome|Unable to sign up')
  } finally {
    loadingSignUp.value = false
  }
}

const submitRequestCode = async () => {
  clearMessages()
  loadingRequestCode.value = true
  try {
    const normalizedLogin = String(signInForm.value.login || '')
      .trim()
      .replace(/^@+/, '')
    signInForm.value.login = normalizedLogin

    const response = await requestCodeMutate({
      login: normalizedLogin,
    })
    const result = response?.data?.requestEmailSignInCode
    if (!result?.success) {
      authError.value = result?.errors?.[0] || s__('Welcome|Unable to send code')
      return
    }

    challengeId.value = result?.challengeId || null
    resendAvailableAt.value = result?.resendAvailableAt || null
    if (!challengeId.value) {
      authSuccessMessage.value = s__('Welcome|If the account exists, a sign-in code was sent.')
      return
    }

    authSuccessMessage.value = s__('Welcome|A sign-in code was sent to your email.')
    switchMode('code', true)
  } catch (e) {
    authError.value = e.message || s__('Welcome|Unable to send code')
  } finally {
    loadingRequestCode.value = false
  }
}

const submitVerifyCode = async () => {
  clearMessages()
  if (!challengeId.value) {
    authError.value = s__('Welcome|Please request a sign-in code first.')
    switchMode('signin')
    return
  }

  loadingVerifyCode.value = true
  try {
    const response = await verifyCodeMutate({
      challengeId: challengeId.value,
      code: signInForm.value.code,
    })
    const result = response?.data?.verifyEmailSignInCode
    if (!result?.success) {
      authError.value = result?.errors?.[0] || s__('Welcome|Invalid code')
      return
    }

    window.location.reload()
  } catch (e) {
    authError.value = e.message || s__('Welcome|Unable to verify code')
  } finally {
    loadingVerifyCode.value = false
  }
}

const confirmEmailFromQuery = async () => {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('email_confirmation_token')
  if (!token) return

  clearMessages()
  try {
    const response = await confirmEmailMutate({ token })
    const result = response?.data?.confirmEmail
    if (!result?.success) {
      authError.value = result?.errors?.[0] || s__('Welcome|Email confirmation failed')
      return
    }

    const cleanUrl = `${window.location.pathname}${window.location.hash}`
    window.location.replace(cleanUrl)
  } catch (e) {
    authError.value = e.message || s__('Welcome|Email confirmation failed')
  }
}

onMounted(() => {
  applyLocale(detectLocale())
  const params = new URLSearchParams(window.location.search)
  if (params.get('email_confirmation_token')) {
    languageStepCompleted.value = true
  }
  confirmEmailFromQuery()
})

watch(
  telegramOnboardingState,
  state => {
    if (!state?.pending) return
    signUpForm.value.username = state.username || signUpForm.value.username
    signUpForm.value.fullName = state.fullName || signUpForm.value.fullName
    if (state.preferredLanguage) {
      applyLocale(state.preferredLanguage)
    }
  },
  { immediate: true }
)
</script>

<style src="../../styles/mobile-stretch-buttons.css"></style>
<style scoped>
.welcome-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.welcome-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
  overflow: hidden;
}

.info-section {
  padding: 40px 32px;
}

.info-text {
  text-align: center;
  font-size: 18px;
  color: #2d3748;
  margin: 0 0 32px;
  font-weight: 500;
}

.language-switch {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin: 0 0 8px;
}

.language-switch-button {
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #666;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.language-switch-button.selected,
.language-switch-button:hover {
  border-color: #2aabee;
  background: #2aabee;
  color: #fff;
}

.language-step {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  padding: 20px;
  margin-bottom: 24px;
  text-align: center;
}

.language-step-title {
  margin: 0 0 8px;
  color: #333;
  font-size: 22px;
}

.language-step-description {
  margin: 0 0 16px;
  color: #666;
  font-size: 14px;
}

.language-step-options {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
}

.language-option {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #333;
  padding: 8px 14px;
  cursor: pointer;
}

.language-option.selected,
.language-option:hover {
  border-color: #2aabee;
  background: #2aabee;
  color: #fff;
}

.telegram-auth-methods {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
  margin-bottom: 24px;
}

.telegram-auth-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.telegram-auth-title {
  margin: 0 0 12px;
  color: #333;
  font-size: 18px;
}

.telegram-auth-method {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  background: #f7fafc;
  transition: all 0.3s ease;
}

.method-icon {
  font-size: 40px;
  text-align: center;
  margin-bottom: 16px;
}

.method-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px;
  text-align: center;
}

.method-steps {
  margin: 0;
  padding-left: 20px;
  color: #4a5568;
  line-height: 1.8;
}

.method-steps li {
  margin-bottom: 8px;
}

.method-steps li:last-child {
  margin-bottom: 0;
}

.help-section {
  background: #edf2f7;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.help-text {
  margin: 0;
  color: #4a5568;
  font-size: 14px;
  line-height: 1.6;
}

.email-auth-section {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
  margin-bottom: 24px;
}

.email-auth-title {
  margin: 0 0 12px;
  color: #333;
  font-size: 18px;
}

.email-auth-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.email-auth-button {
  border: 1px solid #2aabee;
  background: #2aabee;
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.email-auth-button.secondary {
  background: #fff;
  color: #2aabee;
}

.email-auth-button:disabled,
.email-auth-submit:disabled,
.email-auth-cancel:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.email-auth-form {
  display: grid;
  gap: 10px;
}

.email-auth-label {
  color: #333;
  display: grid;
  gap: 6px;
  font-size: 14px;
}

.email-auth-input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  min-height: 36px;
  padding: 6px 10px;
}

.email-auth-form-actions {
  display: flex;
  gap: 8px;
}

.email-auth-submit {
  border: 1px solid #2aabee;
  background: #2aabee;
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.email-auth-cancel {
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.auth-error {
  color: #dc2626;
  margin-bottom: 8px;
}

.auth-notice {
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

@media (max-width: 640px) {
  .info-section {
    padding: 32px 20px;
  }

  .telegram-auth-methods-grid {
    grid-template-columns: 1fr;
  }

  .email-auth-actions {
    flex-direction: column;
  }

  .email-auth-actions.mobile-stretch-btn-group > button {
    flex: 1 1 100%;
  }

  .language-step-options {
    flex-direction: column;
  }
}

/* Dark theme support */
@media (prefers-color-scheme: dark) {
  .welcome-container {
    background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  }

  .welcome-card {
    background: #2d3748;
  }

  .info-text {
    color: #e2e8f0;
  }

  .language-step {
    background: #1a202c;
    border-color: #4a5568;
  }

  .language-step-title {
    color: #e2e8f0;
  }

  .language-step-description {
    color: #cbd5e0;
  }

  .language-option {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .language-switch-button {
    background: #2d3748;
    border-color: #4a5568;
    color: #cbd5e0;
  }

  .telegram-auth-method {
    background: #1a202c;
    border-color: #4a5568;
  }

  .telegram-auth-method:hover {
    border-color: #667eea;
  }

  .telegram-auth-methods {
    background: #1a202c;
    border-color: #4a5568;
  }

  .method-title {
    color: #e2e8f0;
  }

  .telegram-auth-title {
    color: #e2e8f0;
  }

  .method-steps {
    color: #cbd5e0;
  }

  .help-section {
    background: #1a202c;
  }

  .help-text {
    color: #cbd5e0;
  }

  .email-auth-section {
    background: #1a202c;
    border-color: #4a5568;
  }

  .email-auth-title,
  .email-auth-label {
    color: #e2e8f0;
  }

  .email-auth-input {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .email-auth-cancel {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }

  .auth-notice {
    color: #bfdbfe;
    background: #1e293b;
    border-color: #334155;
  }
}
</style>
